import React, { useState, useEffect, useCallback } from 'react';
import { UnreadMessagesContext } from '../../context/UnreadMessagesContext';
import {
  Box,
  Drawer,
  CssBaseline,
} from '@mui/material';
import {
  Calendar,
  Users,
  Storefront,
  CalendarCheck,
  Heart,
  CurrencyGbp,
  ListChecks,
  FileText,
} from '@phosphor-icons/react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import SidebarNavigation, { EXPANDED_WIDTH } from './SidebarNavigation';
import ProfileMenuPopup from './ProfileMenuPopup';
import AppHeader from './AppHeader';
import GhostLoginBanner from '../Admin/GhostLoginBanner';

const _drawerWidth = EXPANDED_WIDTH;

// Menu sections organized as per requirements
const menuSections = [
  {
    title: 'OVERVIEW',
    items: [
      { title: 'Wedding', icon: <Calendar size={24} weight="regular" />, path: '/wedding-details' },
      { title: 'Suppliers', icon: <Storefront size={24} weight="regular" />, path: '/suppliers' },
      {
        title: 'Guests',
        icon: <Users size={24} weight="regular" />,
        children: [
          { title: 'Guest List', path: '/guest-list' },
          { title: 'Wedding Party', path: '/guests/wedding-party' },
          { title: 'Seating Plan', path: '/seating-plan' },
        ],
      },
      { title: 'To-Do List', icon: <ListChecks size={24} weight="regular" />, path: '/todo-list' },
      { title: 'Bookings', icon: <CalendarCheck size={24} weight="regular" />, path: '/bookings' },
      { title: 'Agreements', icon: <FileText size={24} weight="regular" />, path: '/documents' },
      { title: 'Budget', icon: <CurrencyGbp size={24} weight="regular" />, path: '/budget' },
      { title: 'Vows', icon: <Heart size={24} weight="regular" />, path: '/vows' },
    ]
  }
];

const fetchUserProfile = async () => {
  try {
    const [userResponse, weddingResponse] = await Promise.all([
      api.get('/api/auth/me'),
      api.get('/api/wedding')
    ]);
    
    return {
      ...userResponse.data,
      wedding: weddingResponse.data
    };
  } catch (error) {
    throw error;
  }
};

function MainLayout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { wedding } = useAuth();
  const { notifications, unreadCount, markAsRead, markAllAsRead, deleteAllNotifications, deleteNotification } = useNotifications();

  const refreshUnreadMessagesCount = useCallback(async () => {
    try {
      const response = await api.get('/api/chat/conversations');
      const conversations = Array.isArray(response.data) ? response.data : [];
      const total = conversations.reduce((sum, c) => sum + (c.unreadCounts?.couple || 0), 0);
      setUnreadMessagesCount(total);
    } catch {
      setUnreadMessagesCount(0);
    }
  }, []);

  useEffect(() => {
    refreshUnreadMessagesCount();
    const onVisibilityChange = () => { if (document.visibilityState === 'visible') refreshUnreadMessagesCount(); };
    document.addEventListener('visibilitychange', onVisibilityChange);
    const interval = setInterval(refreshUnreadMessagesCount, 60000);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      clearInterval(interval);
    };
  }, [refreshUnreadMessagesCount]);

  const unreadMessagesValue = React.useMemo(
    () => ({ unreadMessagesCount, refreshUnreadMessagesCount }),
    [unreadMessagesCount, refreshUnreadMessagesCount]
  );
  
  // Fixed icon-only sidebar width (no collapse)
  const currentDrawerWidth = EXPANDED_WIDTH;

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const profile = await fetchUserProfile();
        setUserProfile(profile);
      } catch (error) {
        // Handle error - maybe redirect to login if unauthorized
        if (error.response?.status === 401) {
          navigate('/login');
        }
      }
    };
    loadProfile();
  }, [navigate]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const _handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  // No collapse functionality - always icon-only

  const isActive = useCallback((path) => {
    if (!path) return false;
    if (path === '/dashboard') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  }, [location.pathname]);

  // Track last visited section for dashboard "Continue where you left off"
  useEffect(() => {
    const currentPath = location.pathname;
    // Don't track dashboard, login, or register pages
    if (currentPath !== '/dashboard' && 
        !currentPath.startsWith('/login') && 
        !currentPath.startsWith('/register') &&
        !currentPath.startsWith('/supplier') &&
        !currentPath.startsWith('/admin')) {
      
      const sectionMap = {
        '/todos': 'To-Do List',
        '/todo-list': 'To-Do List',
        '/chat': 'Messages',
        '/guest-list': 'Guest List',
        '/wedding-details': 'Wedding Details',
        '/timeline': 'Timeline',
        '/budget': 'Budget Tracker',
        '/wedding/budget': 'Budget Tracker',
        '/partner-details': 'Partner Details',
        '/couple-details': 'Partner Details',
        '/couple-profile': 'Wedding',
        '/supplier-search': 'Find Suppliers',
        '/suppliers': 'Your Suppliers',
        '/seating-plan': 'Seating Plan',
        '/guests/wedding-party': 'Wedding Party',
      };
      
      const sectionName = sectionMap[currentPath] || 
                         currentPath.split('/').pop().replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      
      const sectionData = {
        path: currentPath,
        timestamp: Date.now(),
        name: sectionName
      };
      
      localStorage.setItem('lastVisitedSection', JSON.stringify(sectionData));
    }
  }, [location.pathname]);

  const logoComponent = (
    <img 
      src={`${process.env.PUBLIC_URL}/img/logo.svg`}
      alt="Logo" 
      style={{ height: 30 }} 
    />
  );

  const logoIconComponent = (
    <img 
      src={`${process.env.PUBLIC_URL}/img/logoicon.svg`}
      alt="Logo Icon" 
      style={{ height: 30 }} 
    />
  );

  const profileData = userProfile ? {
    firstName: userProfile.firstName,
    lastName: userProfile.lastName,
    email: userProfile.email,
    profilePicture: userProfile.profilePicture,
    membershipLevel: wedding?.membershipLevel || userProfile.membershipLevel || 'free',
  } : null;

  return (
    <UnreadMessagesContext.Provider value={unreadMessagesValue}>
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <Box
        component="nav"
        sx={{ 
          width: { md: currentDrawerWidth }, 
          flexShrink: { md: 0 },
          position: 'fixed',
          height: '100vh',
          transition: 'width 0.3s ease-in-out',
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: EXPANDED_WIDTH,
            },
          }}
        >
          <SidebarNavigation
            sections={menuSections}
            userProfile={profileData}
            onProfileMenuOpen={(e) => setAnchorEl(e.currentTarget)}
            logoComponent={logoComponent}
            logoIconComponent={logoIconComponent}
            isActive={isActive}
            notifications={notifications}
            unreadCount={unreadCount}
            onMarkAsRead={markAsRead}
            onMarkAllAsRead={markAllAsRead}
            onDeleteAllNotifications={deleteAllNotifications}
            onDeleteNotification={deleteNotification}
          />
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: `${currentDrawerWidth}px !important`,
              minWidth: `${currentDrawerWidth}px !important`,
              maxWidth: `${currentDrawerWidth}px !important`,
              position: 'fixed',
              height: '100vh',
              transition: 'width 0.3s ease-in-out',
              overflowX: 'hidden',
              backgroundColor: 'transparent', // No background
            },
          }}
          open
        >
          <SidebarNavigation
            sections={menuSections}
            userProfile={profileData}
            onProfileMenuOpen={(e) => setAnchorEl(e.currentTarget)}
            logoComponent={logoComponent}
            logoIconComponent={logoIconComponent}
            isActive={isActive}
            notifications={notifications}
            unreadCount={unreadCount}
            onMarkAsRead={markAsRead}
            onMarkAllAsRead={markAllAsRead}
            onDeleteAllNotifications={deleteAllNotifications}
            onDeleteNotification={deleteNotification}
          />
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100vw - ${currentDrawerWidth}px)` },
          marginLeft: { xs: 0, md: `${currentDrawerWidth}px` },
          marginRight: 0,
          bgcolor: 'transparent',
          minHeight: '100vh',
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          transition: 'margin-left 0.3s ease-in-out, width 0.3s ease-in-out, margin-right 0.3s ease-in-out',
        }}
      >
        <GhostLoginBanner />
        <AppHeader 
          isSupplier={false} 
          notifications={notifications}
          unreadCount={unreadCount}
          unreadMessagesCount={unreadMessagesCount}
          onNotificationClick={() => navigate('/notifications')}
        />
        <Box
          sx={{
            flex: 1,
            p: 3,
            width: '100%',
            maxWidth: { sm: '100%', md: '1200px' },
            mx: 'auto',
          }}
        >
          {children}
        </Box>
      </Box>
      <ProfileMenuPopup
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        userProfile={profileData}
        onLogout={handleLogout}
      />
    </Box>
    </UnreadMessagesContext.Provider>
  );
}

export default MainLayout; 